import React from 'react';
import {
  ArrowRight,
  CalendarDays,
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
  Info,
  Loader2,
} from 'lucide-react';
import { PDFDocument, PDFFont, PDFPage, StandardFonts, rgb } from 'pdf-lib';
import { saveAs } from 'file-saver';

import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { toast } from '@/components/ui/toast';

import './Medcall2ParticipantReports.css';

type MedcallInjectionInfo = {
  date_given?: string | null;
};

type MedcallTimeline = {
  date?: string | null;
  injection_info?: MedcallInjectionInfo[] | null;
  route?: string | null;
  status?: string | null;
};

export type MedcallReportMedication = {
  active?: boolean | null;
  days_of_week?: string[] | null;
  frequency?: string | null;
  importance_level?: number | null;
  injection_info?: MedcallInjectionInfo[] | null;
  medicine_name?: string | null;
  prn?: boolean | null;
  route?: string | null;
  strength?: string | null;
  timelines?: MedcallTimeline[] | null;
  times_per_day?: string | null;
};

type MedcallProjectSitesStatus = {
  date?: string | null;
  status?: string | null;
};

export type MedcallReportParticipant = {
  dob?: string | null;
  firstname?: string | null;
  lastname?: string | null;
  medications?: MedcallReportMedication[] | null;
  program?: string | null;
  project_sites_medication_statuses?: MedcallProjectSitesStatus[] | null;
};

type MedcallMedicationListRow = {
  id: string;
  label: string;
};

export type MedcallMedicationListModel = {
  participantLabel: string;
  prn: MedcallMedicationListRow[];
  regular: MedcallMedicationListRow[];
};

export type MedcallAdherenceDay = {
  date: string;
  label: string;
  value: string;
};

export type MedcallAdherenceMedication = {
  adherence: number | null;
  days: MedcallAdherenceDay[];
  frequency: string;
  importance: string;
  medicineName: string;
  route: string;
  strength: string;
};

export type MedcallAdherenceReportModel = {
  endDate: string;
  generatedAt: Date;
  medications: MedcallAdherenceMedication[];
  participantDob: string;
  participantName: string;
  startDate: string;
  totalAdherence: number | null;
};

type PrnChoice = 'all' | 'no' | 'yes';
type ImportanceChoice = 'High' | 'Low' | 'Medium';

const IMPORTANCE_LEVELS: Record<ImportanceChoice, number> = {
  High: 2,
  Low: 0,
  Medium: 1,
};
const IMPORTANCE_CHOICES = Object.keys(IMPORTANCE_LEVELS) as ImportanceChoice[];
const LEGACY_IMPORTANCE_CHOICES: ImportanceChoice[] = ['Low', 'Medium', 'High'];
const MAX_REPORT_DATE_DIFFERENCE_DAYS = 30;
const PDF_PAGE_WIDTH = 1191;
const PDF_PAGE_HEIGHT = 842;
const PDF_MARGIN = 40;
const REPORT_TIMELINE_WIDTH = 550;
const LEGACY_CALENDAR_WEEKDAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
const LEGACY_MONTH_YEAR_FORMATTER = new Intl.DateTimeFormat('en-US', {
  month: 'long',
  timeZone: 'UTC',
  year: 'numeric',
});
const LEGACY_FULL_DATE_FORMATTER = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  timeZone: 'UTC',
  year: 'numeric',
});

const normalizeValue = (value: unknown) => `${value || ''}`.trim();

const parseDateOnly = (value: string) => {
  const match = normalizeValue(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) {
    return null;
  }

  const date = new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])));
  if (Number.isNaN(date.getTime())) {
    return null;
  }

  return date;
};

const formatDateOnly = (date: Date) => {
  const year = date.getUTCFullYear();
  const month = `${date.getUTCMonth() + 1}`.padStart(2, '0');
  const day = `${date.getUTCDate()}`.padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const formatDateLabel = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const match = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) {
    return normalizedValue;
  }

  return `${match[2]}/${match[3]}/${match[1]}`;
};

const formatLegacyShortDateLabel = (value: string | null | undefined) => {
  const normalizedValue = normalizeValue(value);
  const databaseDateMatch = normalizedValue.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (databaseDateMatch) {
    return `${databaseDateMatch[2]}/${databaseDateMatch[3]}/${databaseDateMatch[1].slice(-2)}`;
  }

  const displayDateMatch = normalizedValue.match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (displayDateMatch) {
    return `${displayDateMatch[1]}/${displayDateMatch[2]}/${displayDateMatch[3].slice(-2)}`;
  }

  return normalizedValue;
};

const getUtcMonthStart = (date: Date) => new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), 1));

const shiftUtcMonth = (date: Date, monthOffset: number) => new Date(
  Date.UTC(date.getUTCFullYear(), date.getUTCMonth() + monthOffset, 1),
);

type LegacyCalendarDay = {
  date: Date;
  dateValue: string;
  inCurrentMonth: boolean;
};

const buildLegacyCalendarDays = (month: Date): LegacyCalendarDay[] => {
  const monthStart = getUtcMonthStart(month);
  const gridStart = new Date(monthStart.getTime());
  gridStart.setUTCDate(gridStart.getUTCDate() - monthStart.getUTCDay());

  return Array.from({ length: 42 }, (_, index) => {
    const date = new Date(gridStart.getTime());
    date.setUTCDate(gridStart.getUTCDate() + index);
    return {
      date,
      dateValue: formatDateOnly(date),
      inCurrentMonth: date.getUTCMonth() === monthStart.getUTCMonth(),
    };
  });
};

const getLegacyCalendarDateClasses = ({
  dateValue,
  disabled,
  endDate,
  inCurrentMonth,
  startDate,
  today,
}: {
  dateValue: string;
  disabled: boolean;
  endDate: string;
  inCurrentMonth: boolean;
  startDate: string;
  today: string;
}) => {
  const classNames = ['medcall2-legacy-participant-report-calendar__day'];
  if (!inCurrentMonth) {
    classNames.push('is-outside-month');
  }
  if (dateValue >= startDate && dateValue <= endDate) {
    classNames.push('is-in-range');
  }
  if (dateValue === startDate || dateValue === endDate) {
    classNames.push('is-range-boundary');
  }
  if (dateValue === today) {
    classNames.push('is-today');
  }
  if (disabled) {
    classNames.push('is-disabled');
  }
  return classNames.join(' ');
};

const getLosAngelesDateOnly = (value: Date = new Date()) => {
  const parts = new Intl.DateTimeFormat('en-CA', {
    day: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).formatToParts(value);
  const lookup = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${lookup.year}-${lookup.month}-${lookup.day}`;
};

const enumerateDateRange = (startDate: string, endDate: string) => {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end || start.getTime() > end.getTime()) {
    return [];
  }

  const dates: string[] = [];
  const cursor = new Date(start.getTime());
  while (cursor.getTime() <= end.getTime()) {
    dates.push(formatDateOnly(cursor));
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return dates;
};

export const getMedcallReportDateDifference = (startDate: string, endDate: string) => {
  const start = parseDateOnly(startDate);
  const end = parseDateOnly(endDate);
  if (!start || !end) {
    return null;
  }

  return Math.round((end.getTime() - start.getTime()) / 86_400_000);
};

const formatDayHeader = (date: string) => {
  const parsed = parseDateOnly(date);
  if (!parsed) {
    return '';
  }

  const weekday = new Intl.DateTimeFormat('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
  }).format(parsed).charAt(0);
  return `${weekday}\n${`${parsed.getUTCDate()}`.padStart(2, '0')}`;
};

const formatMedicationFrequency = (medication: MedcallReportMedication) => {
  const frequency = normalizeValue(medication.frequency);
  if (frequency === 'everyotherday') {
    return 'QOD';
  }

  if (frequency === 'custom_days') {
    const days = Array.isArray(medication.days_of_week) ? medication.days_of_week.join(', ') : '';
    return `Custom Week Days: ${days} - ${normalizeValue(medication.times_per_day)}`;
  }

  if (frequency === 'everyday') {
    return normalizeValue(medication.times_per_day);
  }

  return frequency;
};

const formatMedicationName = (medication: MedcallReportMedication) => {
  const medicineName = normalizeValue(medication.medicine_name);
  const strength = normalizeValue(medication.strength);
  if (!strength) {
    return medicineName;
  }

  return medicineName.replace(strength, '').trim();
};

const getLastGivenDate = (medication: MedcallReportMedication) => {
  const timelines = Array.isArray(medication.timelines) ? medication.timelines : [];
  const givenTimeline = timelines.find((timeline) => timeline.status === 'taken');
  return formatDateLabel(givenTimeline?.date);
};

const buildMedicationListRow = (medication: MedcallReportMedication, index: number): MedcallMedicationListRow => {
  const lastGivenDate = getLastGivenDate(medication);
  const lastGivenLabel = lastGivenDate ? `, Last Given Date: ${lastGivenDate}` : '';
  const label = `${formatMedicationName(medication)}, ${normalizeValue(medication.strength)}, ${normalizeValue(medication.route)}, ${formatMedicationFrequency(medication).toUpperCase()}${lastGivenLabel}`;
  return {
    id: `${index}-${normalizeValue(medication.medicine_name)}-${normalizeValue(medication.strength)}`,
    label,
  };
};

export const buildMedcallMedicationListModel = (
  participant: MedcallReportParticipant
): MedcallMedicationListModel => {
  const medications = Array.isArray(participant.medications) ? participant.medications : [];
  const activeMedications = medications.filter((medication) => Boolean(medication.active));
  const regular = activeMedications
    .filter((medication) => !Boolean(medication.prn))
    .map(buildMedicationListRow);
  const prn = activeMedications
    .filter((medication) => Boolean(medication.prn))
    .map(buildMedicationListRow);

  return {
    participantLabel: `${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)} - ${formatDateLabel(participant.dob)}`.trim(),
    prn,
    regular,
  };
};

const buildLegacyMedicationListRow = (
  medication: MedcallReportMedication,
  index: number,
): MedcallMedicationListRow => {
  const timelines = Array.isArray(medication.timelines) ? medication.timelines : [];
  const lastGivenTimeline = timelines.find((timeline) => timeline.status === 'taken');
  const lastGivenDate = formatLegacyShortDateLabel(lastGivenTimeline?.date);
  const lastGivenLabel = lastGivenDate ? `, Last Given Date: ${lastGivenDate}` : '';
  const label = `${formatMedicationName(medication)}, ${normalizeValue(medication.strength)}, ${normalizeValue(medication.route)}, ${formatMedicationFrequency(medication).toUpperCase()}${lastGivenLabel}`;
  return {
    id: `legacy-${index}-${normalizeValue(medication.medicine_name)}-${normalizeValue(medication.strength)}`,
    label,
  };
};

const buildLegacyMedicationListModel = (
  participant: MedcallReportParticipant,
): MedcallMedicationListModel => {
  const medications = Array.isArray(participant.medications) ? participant.medications : [];
  const activeMedications = medications.filter((medication) => Number(medication.active) === 1);
  const regular = activeMedications
    .filter((medication) => Number(medication.prn) === 0)
    .map(buildLegacyMedicationListRow);
  const prn = activeMedications
    .filter((medication) => Number(medication.prn) === 1)
    .map(buildLegacyMedicationListRow);

  return {
    participantLabel: `${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)} - ${formatLegacyShortDateLabel(participant.dob)}`.trim(),
    prn,
    regular,
  };
};

const timelineWasTaken = (timeline: MedcallTimeline) => {
  if (normalizeValue(timeline.route) !== 'IM-LAI') {
    return timeline.status === 'taken';
  }

  const injectionInfo = Array.isArray(timeline.injection_info) ? timeline.injection_info[0] : null;
  return normalizeValue(injectionInfo?.date_given).length > 0;
};

const buildTimelineAdherenceDays = (
  medication: MedcallReportMedication,
  dates: string[],
  today: string
) => {
  const timelines = Array.isArray(medication.timelines) ? medication.timelines : [];
  let taken = 0;
  let total = 0;
  const days = dates.map((date) => {
    const matchingTimelines = timelines.filter((timeline) => timeline.date === date && timeline.status !== 'refill');
    const values = matchingTimelines.map((timeline) => timelineWasTaken(timeline) ? 'Y' : 'N');

    if (date < today) {
      matchingTimelines.forEach((timeline) => {
        total += 1;
        if (timelineWasTaken(timeline)) {
          taken += 1;
        }
      });
    }

    return {
      date,
      label: formatDayHeader(date),
      value: values.join('\n'),
    };
  });

  return {
    adherence: total > 0 ? Math.ceil((100 * taken) / total) : null,
    days,
    total,
  };
};

const buildProjectSitesAdherenceDays = (
  medication: MedcallReportMedication,
  dates: string[],
  statuses: MedcallProjectSitesStatus[]
) => {
  let taken = 0;
  let total = 0;
  const injectionInfo = Array.isArray(medication.injection_info) ? medication.injection_info[0] : null;
  const injectionWasGiven = normalizeValue(injectionInfo?.date_given).length > 0;
  const days = dates.map((date) => {
    const status = statuses.find((candidate) => candidate.date === date);
    const statusWasTaken = status?.status === 'taken';
    const wasTaken = statusWasTaken || injectionWasGiven;
    total += 1;
    if (wasTaken) {
      taken += 1;
    }

    return {
      date,
      label: formatDayHeader(date),
      value: wasTaken ? 'Y' : 'N',
    };
  });

  return {
    adherence: total > 0 ? Math.ceil((100 * taken) / total) : null,
    days,
    total,
  };
};

export const buildMedcallAdherenceReportModel = ({
  endDate,
  importanceChoices,
  participant,
  prnChoice,
  startDate,
  today = getLosAngelesDateOnly(),
}: {
  endDate: string;
  importanceChoices: ImportanceChoice[];
  participant: MedcallReportParticipant;
  prnChoice: PrnChoice;
  startDate: string;
  today?: string;
}): MedcallAdherenceReportModel => {
  const dates = enumerateDateRange(startDate, endDate);
  const importanceValues = importanceChoices.map((choice) => IMPORTANCE_LEVELS[choice]);
  const medications = (Array.isArray(participant.medications) ? participant.medications : [])
    .filter((medication) => Boolean(medication.active))
    .filter((medication) => {
      if (prnChoice === 'all') {
        return true;
      }

      return Boolean(medication.prn) === (prnChoice === 'yes');
    })
    .filter((medication) => importanceValues.includes(Number(medication.importance_level)));
  const projectSitesStatuses = Array.isArray(participant.project_sites_medication_statuses)
    ? participant.project_sites_medication_statuses
    : [];
  const isProjectSites = participant.program === 'Project Sites';
  let adherenceTotal = 0;
  let medicationsWithAdherence = 0;

  const reportMedications = medications.map((medication) => {
    const adherenceDays = isProjectSites
      ? buildProjectSitesAdherenceDays(medication, dates, projectSitesStatuses)
      : buildTimelineAdherenceDays(medication, dates, today);
    if (adherenceDays.total > 0 && adherenceDays.adherence !== null) {
      adherenceTotal += adherenceDays.adherence;
      medicationsWithAdherence += 1;
    }

    const importanceName = IMPORTANCE_CHOICES.find((choice) => {
      return IMPORTANCE_LEVELS[choice] === Number(medication.importance_level);
    }) || '';

    return {
      adherence: adherenceDays.adherence,
      days: adherenceDays.days,
      frequency: formatMedicationFrequency(medication).toUpperCase(),
      importance: `${importanceName} Importance`,
      medicineName: normalizeValue(medication.medicine_name).replace(` ${normalizeValue(medication.strength)}`, ''),
      route: normalizeValue(medication.route),
      strength: normalizeValue(medication.strength),
    };
  });

  return {
    endDate,
    generatedAt: new Date(),
    medications: reportMedications,
    participantDob: formatDateLabel(participant.dob),
    participantName: `${normalizeValue(participant.firstname)} ${normalizeValue(participant.lastname)}`.trim(),
    startDate,
    totalAdherence: medicationsWithAdherence > 0
      ? Math.ceil(adherenceTotal / medicationsWithAdherence)
      : null,
  };
};

const drawTextLines = ({
  font,
  fontSize,
  lineHeight,
  page,
  text,
  x,
  y,
}: {
  font: PDFFont;
  fontSize: number;
  lineHeight: number;
  page: PDFPage;
  text: string;
  x: number;
  y: number;
}) => {
  text.split('\n').forEach((line, lineIndex) => {
    page.drawText(line, {
      font,
      size: fontSize,
      x,
      y: y - lineIndex * lineHeight,
    });
  });
};

const drawReportHeader = ({
  boldFont,
  font,
  model,
  page,
}: {
  boldFont: PDFFont;
  font: PDFFont;
  model: MedcallAdherenceReportModel;
  page: PDFPage;
}) => {
  page.drawText(model.participantName, {
    font: boldFont,
    size: 20,
    x: PDF_MARGIN,
    y: PDF_PAGE_HEIGHT - 42,
  });
  page.drawText(`DOB: ${model.participantDob}`, {
    font,
    size: 12,
    x: PDF_MARGIN,
    y: PDF_PAGE_HEIGHT - 61,
  });
  const generatedLabel = new Intl.DateTimeFormat('en-US', {
    day: '2-digit',
    hour: 'numeric',
    minute: '2-digit',
    month: '2-digit',
    timeZone: 'America/Los_Angeles',
    year: 'numeric',
  }).format(model.generatedAt);
  const reportDatesLabel = `Report Dates: ${formatDateLabel(model.startDate)} - ${formatDateLabel(model.endDate)}`;
  page.drawText(generatedLabel, {
    font: boldFont,
    size: 12,
    x: PDF_PAGE_WIDTH - PDF_MARGIN - boldFont.widthOfTextAtSize(generatedLabel, 12),
    y: PDF_PAGE_HEIGHT - 42,
  });
  page.drawText(reportDatesLabel, {
    font,
    size: 10,
    x: PDF_PAGE_WIDTH - PDF_MARGIN - font.widthOfTextAtSize(reportDatesLabel, 10),
    y: PDF_PAGE_HEIGHT - 58,
  });
  page.drawLine({
    color: rgb(0, 0, 0),
    end: { x: PDF_PAGE_WIDTH - PDF_MARGIN, y: PDF_PAGE_HEIGHT - 70 },
    start: { x: PDF_MARGIN, y: PDF_PAGE_HEIGHT - 70 },
    thickness: 1,
  });
};

export const createMedcallAdherenceReportPdf = async (model: MedcallAdherenceReportModel) => {
  const pdf = await PDFDocument.create();
  const font = await pdf.embedFont(StandardFonts.TimesRoman);
  const boldFont = await pdf.embedFont(StandardFonts.TimesRomanBold);
  let page = pdf.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
  drawReportHeader({ boldFont, font, model, page });
  let y = PDF_PAGE_HEIGHT - 92;

  model.medications.forEach((medication) => {
    const rowHeight = 88;
    if (y - rowHeight < 55) {
      page = pdf.addPage([PDF_PAGE_WIDTH, PDF_PAGE_HEIGHT]);
      drawReportHeader({ boldFont, font, model, page });
      y = PDF_PAGE_HEIGHT - 92;
    }

    const nameWidth = 220;
    const frequencyWidth = 150;
    const timelineStart = PDF_MARGIN + nameWidth + frequencyWidth;
    const dayWidth = medication.days.length > 0
      ? REPORT_TIMELINE_WIDTH / medication.days.length
      : REPORT_TIMELINE_WIDTH;
    const adherenceX = timelineStart + REPORT_TIMELINE_WIDTH;
    page.drawRectangle({
      borderColor: rgb(0.55, 0.55, 0.55),
      borderWidth: 0.5,
      height: rowHeight,
      width: PDF_PAGE_WIDTH - PDF_MARGIN * 2,
      x: PDF_MARGIN,
      y: y - rowHeight,
    });
    drawTextLines({
      font,
      fontSize: 9,
      lineHeight: 13,
      page,
      text: `${medication.medicineName}\n${medication.strength}\n${medication.route}\n${medication.importance}`,
      x: PDF_MARGIN + 8,
      y: y - 16,
    });
    drawTextLines({
      font,
      fontSize: 9,
      lineHeight: 12,
      page,
      text: medication.frequency,
      x: PDF_MARGIN + nameWidth + 8,
      y: y - 16,
    });
    medication.days.forEach((day, index) => {
      const cellX = timelineStart + dayWidth * index;
      page.drawRectangle({
        borderColor: rgb(0.72, 0.72, 0.72),
        borderWidth: 0.35,
        height: rowHeight,
        width: dayWidth,
        x: cellX,
        y: y - rowHeight,
      });
      drawTextLines({
        font: boldFont,
        fontSize: 6,
        lineHeight: 8,
        page,
        text: day.label,
        x: cellX + 3,
        y: y - 11,
      });
      drawTextLines({
        font,
        fontSize: 7,
        lineHeight: 9,
        page,
        text: day.value,
        x: cellX + 3,
        y: y - 36,
      });
    });
    page.drawText(medication.adherence === null ? '' : `${medication.adherence}%`, {
      font: boldFont,
      size: 11,
      x: adherenceX + 12,
      y: y - 47,
    });
    y -= rowHeight;
  });

  const totalLabel = model.totalAdherence === null ? 'N/A' : `Total: ${model.totalAdherence}%`;
  page.drawText(totalLabel, {
    font: boldFont,
    size: 20,
    x: PDF_PAGE_WIDTH - PDF_MARGIN - boldFont.widthOfTextAtSize(totalLabel, 20),
    y: Math.max(24, y - 30),
  });

  return pdf.save();
};

const capitalizeFirstLetter = (value: string) => {
  const normalizedValue = normalizeValue(value);
  if (!normalizedValue) {
    return '';
  }

  return `${normalizedValue.charAt(0).toUpperCase()}${normalizedValue.slice(1)}`;
};

export const buildMedcallAdherenceReportFilename = (participant: MedcallReportParticipant) => {
  const firstName = capitalizeFirstLetter(normalizeValue(participant.firstname));
  const lastName = capitalizeFirstLetter(normalizeValue(participant.lastname));
  const dob = formatDateLabel(participant.dob).replace('/', '_');
  return `${firstName}_${lastName}_${dob}_MedCall_Adherence_Report.pdf`;
};

const LegacyReportCalendarMonth = ({
  endDate,
  month,
  onSelect,
  onShiftMonth,
  panel,
  selectingEnd,
  startDate,
  today,
}: {
  endDate: string;
  month: Date;
  onSelect: (dateValue: string) => void;
  onShiftMonth: (monthOffset: number) => void;
  panel: 'left' | 'right';
  selectingEnd: boolean;
  startDate: string;
  today: string;
}) => {
  const monthLabel = LEGACY_MONTH_YEAR_FORMATTER.format(month);
  const monthToken = formatDateOnly(month).slice(0, 7);
  const days = buildLegacyCalendarDays(month);

  return (
    <section className="medcall2-legacy-participant-report-calendar__month">
      <header className="medcall2-legacy-participant-report-calendar__header">
        {panel === 'left' ? (
          <div className="medcall2-legacy-participant-report-calendar__navigation">
            <button
              aria-label="Previous year"
              id="medcall2-report-calendar-previous-year"
              onClick={() => onShiftMonth(-12)}
              type="button"
            >
              <ChevronsLeft aria-hidden="true" />
            </button>
            <button
              aria-label="Previous month"
              id="medcall2-report-calendar-previous-month"
              onClick={() => onShiftMonth(-1)}
              type="button"
            >
              <ChevronLeft aria-hidden="true" />
            </button>
          </div>
        ) : <span aria-hidden="true" />}
        <strong>{monthLabel}</strong>
        {panel === 'right' ? (
          <div className="medcall2-legacy-participant-report-calendar__navigation">
            <button
              aria-label="Next month"
              id="medcall2-report-calendar-next-month"
              onClick={() => onShiftMonth(1)}
              type="button"
            >
              <ChevronRight aria-hidden="true" />
            </button>
            <button
              aria-label="Next year"
              id="medcall2-report-calendar-next-year"
              onClick={() => onShiftMonth(12)}
              type="button"
            >
              <ChevronsRight aria-hidden="true" />
            </button>
          </div>
        ) : <span aria-hidden="true" />}
      </header>
      <table aria-label={`${monthLabel} calendar`} role="grid">
        <thead>
          <tr>
            {LEGACY_CALENDAR_WEEKDAYS.map((weekday) => <th key={weekday} scope="col">{weekday}</th>)}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: 6 }, (_, weekIndex) => (
            <tr key={`${monthToken}-week-${weekIndex + 1}`}>
              {days.slice(weekIndex * 7, (weekIndex + 1) * 7).map((calendarDay) => {
                const dateDifference = getMedcallReportDateDifference(startDate, calendarDay.dateValue);
                const disabled = selectingEnd
                  && (dateDifference === null || Math.abs(dateDifference) > MAX_REPORT_DATE_DIFFERENCE_DAYS);
                const isBoundary = calendarDay.dateValue === startDate || calendarDay.dateValue === endDate;
                return (
                  <td key={`${monthToken}-${calendarDay.dateValue}`}>
                    <button
                      aria-label={`Choose ${LEGACY_FULL_DATE_FORMATTER.format(calendarDay.date)}`}
                      aria-pressed={isBoundary}
                      className={getLegacyCalendarDateClasses({
                        dateValue: calendarDay.dateValue,
                        disabled,
                        endDate,
                        inCurrentMonth: calendarDay.inCurrentMonth,
                        startDate,
                        today,
                      })}
                      disabled={disabled}
                      id={`medcall2-report-calendar-${monthToken}-${calendarDay.dateValue}`}
                      onClick={() => onSelect(calendarDay.dateValue)}
                      type="button"
                    >
                      {calendarDay.date.getUTCDate()}
                    </button>
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
};

const LegacyReportDateRangePicker = ({
  disabled = false,
  endDate,
  onChange,
  startDate,
  today,
}: {
  disabled?: boolean;
  endDate: string;
  onChange: (startDate: string, endDate: string) => void;
  startDate: string;
  today: string;
}) => {
  const parsedStartDate = parseDateOnly(startDate);
  const [calendarOpen, setCalendarOpen] = React.useState(false);
  const [selectingEnd, setSelectingEnd] = React.useState(false);
  const [visibleMonth, setVisibleMonth] = React.useState(() => getUtcMonthStart(parsedStartDate || new Date()));

  const handleCalendarOpenChange = (nextOpen: boolean) => {
    setCalendarOpen(nextOpen);
    if (!nextOpen) {
      setSelectingEnd(false);
      return;
    }

    const selectedStartDate = parseDateOnly(startDate);
    if (!selectedStartDate) {
      return;
    }

    setVisibleMonth(getUtcMonthStart(selectedStartDate));
  };

  const handleDateSelection = (dateValue: string) => {
    if (!selectingEnd) {
      onChange(dateValue, dateValue);
      setSelectingEnd(true);
      return;
    }

    if (dateValue < startDate) {
      onChange(dateValue, startDate);
    }
    if (dateValue >= startDate) {
      onChange(startDate, dateValue);
    }
    setSelectingEnd(false);
    setCalendarOpen(false);
  };

  const shiftVisibleMonth = (monthOffset: number) => {
    setVisibleMonth((currentMonth) => shiftUtcMonth(currentMonth, monthOffset));
  };

  return (
    <Popover onOpenChange={handleCalendarOpenChange} open={calendarOpen}>
      <PopoverTrigger asChild>
        <button
          aria-label={`Date Range, ${formatDateLabel(startDate)} to ${formatDateLabel(endDate)}`}
          className="medcall2-legacy-participant-report-range"
          disabled={disabled}
          id="medcall2-report-date-range"
          type="button"
        >
          <span>{formatDateLabel(startDate)}</span>
          <ArrowRight aria-hidden="true" className="medcall2-legacy-participant-report-range__separator" />
          <span>{formatDateLabel(endDate)}</span>
          <CalendarDays aria-hidden="true" className="medcall2-legacy-participant-report-range__calendar" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        aria-label="Date Range calendar"
        className="medcall2-legacy-participant-report-calendar"
        onOpenAutoFocus={(event) => event.preventDefault()}
        sideOffset={4}
      >
        <span className="sr-only">{selectingEnd ? 'Choose end date' : 'Choose start date'}</span>
        <div className="medcall2-legacy-participant-report-calendar__months">
          <LegacyReportCalendarMonth
            endDate={endDate}
            month={visibleMonth}
            onSelect={handleDateSelection}
            onShiftMonth={shiftVisibleMonth}
            panel="left"
            selectingEnd={selectingEnd}
            startDate={startDate}
            today={today}
          />
          <LegacyReportCalendarMonth
            endDate={endDate}
            month={shiftUtcMonth(visibleMonth, 1)}
            onSelect={handleDateSelection}
            onShiftMonth={shiftVisibleMonth}
            panel="right"
            selectingEnd={selectingEnd}
            startDate={startDate}
            today={today}
          />
        </div>
      </PopoverContent>
    </Popover>
  );
};

export const MedcallMedicationListDialog = ({
  legacyPresentation = false,
  onOpenChange,
  open,
  participant,
}: {
  legacyPresentation?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  participant: MedcallReportParticipant;
}) => {
  const model = buildMedcallMedicationListModel(participant);
  const legacyModel = buildLegacyMedicationListModel(participant);

  if (legacyPresentation) {
    return (
      <Dialog onOpenChange={onOpenChange} open={open}>
        <DialogContent
          className="medcall2-legacy-participant-med-list-dialog p-0"
          contentHeight="auto"
          contentMaxHeight="calc(100vh - 124px)"
          contentMaxWidth="calc(100vw - 32px)"
          contentTop="100px"
          contentWidth="900px"
          hideCloseButton
          overlayClassName="medcall2-legacy-participant-dialog__overlay"
        >
          <div className="medcall2-legacy-participant-med-list">
            <div className="medcall2-legacy-participant-med-list__main">
              <Info
                aria-label="Information"
                className="medcall2-legacy-participant-med-list__icon"
                role="img"
              />
              <div>
                <DialogTitle className="medcall2-legacy-participant-med-list__title">
                  <span>Med List</span>
                  <em>({legacyModel.participantLabel})</em>
                </DialogTitle>
                <DialogDescription className="sr-only">Current regular and PRN medications.</DialogDescription>
                <div className="medcall2-legacy-participant-med-list__content">
                  <section>
                    <strong>Regular</strong>
                    <hr />
                    {legacyModel.regular.map((medication, index) => (
                      <p key={medication.id}>{index + 1}. {medication.label}</p>
                    ))}
                  </section>

                  <section>
                    <strong>PRN</strong>
                    <hr />
                    {legacyModel.prn.map((medication, index) => (
                      <p key={medication.id}>{index + 1}. {medication.label}</p>
                    ))}
                  </section>
                </div>
              </div>
            </div>
            <div className="medcall2-legacy-participant-med-list__footer">
              <Button
                className="medcall2-legacy-participant-button medcall2-legacy-participant-button--primary"
                id="medcall2-med-list-ok"
                onClick={() => onOpenChange(false)}
                type="button"
              >
                OK
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="sm:max-w-4xl">
        <DialogHeader>
          <DialogTitle>
            <span className="font-bold underline">Med List</span>
            <span className="ml-2 text-[15px] font-normal italic">({model.participantLabel})</span>
          </DialogTitle>
          <DialogDescription className="sr-only">Current regular and PRN medications.</DialogDescription>
        </DialogHeader>

        <div className="space-y-6 text-sm text-[var(--neutral-gray-120)]">
          <section>
            <strong>Regular</strong>
            <hr className="my-2 border-[var(--neutral-gray-50)]" />
            {model.regular.map((medication, index) => (
              <p key={medication.id} className="my-2">{index + 1}. {medication.label}</p>
            ))}
          </section>

          <section>
            <strong>PRN</strong>
            <hr className="my-2 border-[var(--neutral-gray-50)]" />
            {model.prn.map((medication, index) => (
              <p key={medication.id} className="my-2">{index + 1}. {medication.label}</p>
            ))}
          </section>
        </div>

        <DialogFooter>
          <Button onClick={() => onOpenChange(false)} type="button">OK</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export const MedcallAdherenceReportDialog = ({
  legacyPresentation = false,
  onOpenChange,
  open,
  participant,
  routeBase,
}: {
  legacyPresentation?: boolean;
  onOpenChange: (open: boolean) => void;
  open: boolean;
  participant: MedcallReportParticipant;
  routeBase?: string;
}) => {
  const isPromotedPage = routeBase === '/tools/medcall' || routeBase === '/tools/medcall2';
  const today = getLosAngelesDateOnly();
  const [startDate, setStartDate] = React.useState(today);
  const [endDate, setEndDate] = React.useState(today);
  const [prnChoice, setPrnChoice] = React.useState<PrnChoice>('all');
  const [importanceChoices, setImportanceChoices] = React.useState<ImportanceChoice[]>(['High']);
  const [generating, setGenerating] = React.useState(false);
  const generationInFlight = React.useRef(false);
  const optionsPending = isPromotedPage && generating;

  const handleOpenChange = (nextOpen: boolean) => {
    if (isPromotedPage && !nextOpen && generationInFlight.current) return;
    onOpenChange(nextOpen);
  };

  React.useEffect(() => {
    if (open) {
      return;
    }

    setStartDate(today);
    setEndDate(today);
    setPrnChoice('all');
    setImportanceChoices(['High']);
    setGenerating(false);
  }, [open, today]);

  const toggleImportance = (choice: ImportanceChoice, checked: boolean) => {
    if (checked) {
      setImportanceChoices((current) => [...current, choice].filter((value, index, values) => values.indexOf(value) === index));
      return;
    }

    setImportanceChoices((current) => current.filter((value) => value !== choice));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (isPromotedPage && generationInFlight.current) return;
    const dateDifference = getMedcallReportDateDifference(startDate, endDate);
    if (dateDifference === null || dateDifference < 0) {
      toast.error('Start date must be on or before end date.');
      return;
    }

    if (dateDifference > MAX_REPORT_DATE_DIFFERENCE_DAYS) {
      toast.error('Date range cannot be longer than 30 days.');
      return;
    }

    if (importanceChoices.length < 1) {
      toast.error('Select at least one importance level.');
      return;
    }

    generationInFlight.current = true;
    setGenerating(true);
    try {
      const model = buildMedcallAdherenceReportModel({
        endDate,
        importanceChoices,
        participant,
        prnChoice,
        startDate,
      });
      const bytes = await createMedcallAdherenceReportPdf(model);
      saveAs(new Blob([bytes], { type: 'application/pdf' }), buildMedcallAdherenceReportFilename(participant));
      onOpenChange(false);
    } catch (reportError: unknown) {
      toast.error(isPromotedPage ? 'Unable to generate the adherence report.' : (reportError as { message?: string } | null)?.message || 'Unable to generate the adherence report.');
    } finally {
      generationInFlight.current = false;
      setGenerating(false);
    }
  };

  return (
    <Dialog onOpenChange={handleOpenChange} open={open}>
      <DialogContent
        className={legacyPresentation ? 'medcall2-legacy-participant-report-dialog p-0' : 'sm:max-w-xl'}
        contentHeight={legacyPresentation ? 'auto' : undefined}
        contentMaxHeight={legacyPresentation ? 'calc(100vh - 124px)' : undefined}
        contentMaxWidth={legacyPresentation ? 'calc(100vw - 32px)' : undefined}
        contentTop={legacyPresentation ? '100px' : undefined}
        contentWidth={legacyPresentation ? '520px' : undefined}
        hideCloseButton={legacyPresentation}
        onEscapeKeyDown={legacyPresentation ? (event) => event.preventDefault() : undefined}
        overlayClassName={legacyPresentation ? 'medcall2-legacy-participant-dialog__overlay' : undefined}
      >
        {legacyPresentation ? (
          <form className="medcall2-legacy-participant-report" onSubmit={handleSubmit}>
            <DialogHeader className="medcall2-legacy-participant-report__header">
              <DialogTitle>Adherence Report</DialogTitle>
              <DialogDescription className="sr-only">Choose the adherence report options.</DialogDescription>
            </DialogHeader>
            <div className="medcall2-legacy-participant-report__body">
              <div className="medcall2-legacy-participant-report__field">
                <span className="medcall2-legacy-participant-report__label">Date Range:</span>
                <LegacyReportDateRangePicker
                  disabled={optionsPending}
                  endDate={endDate}
                  onChange={(nextStartDate, nextEndDate) => {
                    setStartDate(nextStartDate);
                    setEndDate(nextEndDate);
                  }}
                  startDate={startDate}
                  today={today}
                />
              </div>

              <div className="medcall2-legacy-participant-report__field">
                <span className="medcall2-legacy-participant-report__label">PRN:</span>
                <div className="medcall2-legacy-participant-report__radio-group">
                  {([
                    { label: 'All', value: 'all' },
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ] as Array<{ label: string; value: PrnChoice }>).map((option) => (
                    <label
                      className={prnChoice === option.value ? 'is-selected' : ''}
                      htmlFor={`medcall2-report-prn-${option.value}`}
                      key={option.value}
                    >
                      <input
                        checked={prnChoice === option.value}
                        disabled={optionsPending}
                        id={`medcall2-report-prn-${option.value}`}
                        name="medcall-report-prn"
                        onChange={() => setPrnChoice(option.value)}
                        type="radio"
                      />
                      <span>{option.label}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="medcall2-legacy-participant-report__field">
                <span className="medcall2-legacy-participant-report__label">Importance:</span>
                <div className="medcall2-legacy-participant-report__checkbox-group">
                  {LEGACY_IMPORTANCE_CHOICES.map((choice) => {
                    const choiceToken = choice.toLowerCase();
                    return (
                      <label htmlFor={`medcall2-report-importance-${choiceToken}`} key={choice}>
                        <input
                          checked={importanceChoices.includes(choice)}
                          disabled={optionsPending}
                          id={`medcall2-report-importance-${choiceToken}`}
                          onChange={(event) => toggleImportance(choice, event.target.checked)}
                          type="checkbox"
                        />
                        <span>{choice}</span>
                      </label>
                    );
                  })}
                </div>
              </div>

              <div className="medcall2-legacy-participant-report__footer">
                <Button
                  className="medcall2-legacy-participant-button"
                  disabled={optionsPending}
                  id="medcall2-report-cancel"
                  onClick={() => handleOpenChange(false)}
                  type="button"
                  variant="outline"
                >
                  Cancel
                </Button>
                <Button
                  className="medcall2-legacy-participant-button medcall2-legacy-participant-button--primary"
                  disabled={generating}
                  id="medcall2-report-submit"
                  type="submit"
                >
                  {generating ? <Loader2 aria-hidden="true" className="medcall2-legacy-participant-button__spinner" /> : null}
                  Submit
                </Button>
              </div>
            </div>
          </form>
        ) : (
          <form className="space-y-5" onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Adherence Report</DialogTitle>
            <DialogDescription>Select up to 30 days and the medications to include.</DialogDescription>
          </DialogHeader>

          <div className="grid gap-4">
            <fieldset className="grid gap-2 sm:grid-cols-2">
              <legend className="mb-2 text-sm font-medium text-[var(--neutral-gray-120)]">Date Range</legend>
              <label className="grid gap-1 text-sm">
                <span>Start</span>
                <Input disabled={optionsPending} onChange={(event) => setStartDate(event.target.value)} required type="date" value={startDate} />
              </label>
              <label className="grid gap-1 text-sm">
                <span>End</span>
                <Input disabled={optionsPending} onChange={(event) => setEndDate(event.target.value)} required type="date" value={endDate} />
              </label>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-[var(--neutral-gray-120)]">PRN</legend>
              <div className="flex flex-wrap gap-2">
                {([
                  { label: 'All', value: 'all' },
                  { label: 'Yes', value: 'yes' },
                  { label: 'No', value: 'no' },
                ] as Array<{ label: string; value: PrnChoice }>).map((option) => (
                  <label key={option.value} className="flex cursor-pointer items-center gap-2 rounded-md border border-[var(--neutral-gray-40)] px-3 py-2 text-sm">
                    <input
                      checked={prnChoice === option.value}
                      disabled={optionsPending}
                      name="medcall-report-prn"
                      onChange={() => setPrnChoice(option.value)}
                      type="radio"
                    />
                    <span>{option.label}</span>
                  </label>
                ))}
              </div>
            </fieldset>

            <fieldset>
              <legend className="mb-2 text-sm font-medium text-[var(--neutral-gray-120)]">Importance</legend>
              <div className="flex flex-wrap gap-3">
                {IMPORTANCE_CHOICES.map((choice) => (
                  <label key={choice} className="flex cursor-pointer items-center gap-2 text-sm">
                    <Checkbox
                      checked={importanceChoices.includes(choice)}
                      disabled={optionsPending}
                      onCheckedChange={(checked) => toggleImportance(choice, checked === true)}
                    />
                    <span>{choice}</span>
                  </label>
                ))}
              </div>
            </fieldset>
          </div>

          <DialogFooter>
            <Button disabled={generating} onClick={() => handleOpenChange(false)} type="button" variant="outline">
              Cancel
            </Button>
            <Button disabled={generating} type="submit">
              {generating ? 'Generating...' : 'Submit'}
            </Button>
          </DialogFooter>
        </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
